import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRightLeft, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSpare, useSpareStock, useSpareTxns } from "@/features/spares/api";
import { ReceiptDialog } from "@/features/spares/ReceiptDialog";
import { AdjustStockDialog } from "@/features/spares/AdjustStockDialog";
import { SpareTransferDialog } from "@/features/spares/SpareTransferDialog";
import { StockBadge, TxnTypeBadge, ExpiryBadge } from "@/features/spares/badges";
import { SpareForm } from "@/features/spares/SpareForm";
import { ConsumableForm } from "@/features/spares/ConsumableForm";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useSpareAllocations } from "@/features/store-allocations/api";
import { useStoreLookups } from "@/features/store-indents/api";
import { resolveLocationPath, type LocationNode } from "@/features/store-indents/LocationHierarchyPicker";
import { fmtDateTime } from "@/lib/utils";

const WRITE_ROLES = new Set(["super_admin", "biomed_head", "purchase_stores"]);
const TRANSFER_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);

export const Route = createFileRoute("/_authenticated/spares/$spareId")({
  component: SpareDetailPage,
});

function SpareDetailPage() {
  const { spareId } = Route.useParams();
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { data: spare, isLoading } = useSpare(spareId);
  const { data: stock = [] } = useSpareStock(spareId);
  const { data: txns = [] } = useSpareTxns(spareId);
  const { data: allocations = [] } = useSpareAllocations(spareId);
  const { data: lookups } = useStoreLookups();
  const locationNodes = (lookups?.locations ?? []) as LocationNode[];
  const canWrite = (me?.roles ?? []).some((r) => WRITE_ROLES.has(r));
  const canAdjust = (me?.roles ?? []).some((r) => r === "super_admin" || r === "purchase_stores");
  const canTransfer = (me?.roles ?? []).some((r) => TRANSFER_ROLES.has(r));

  if (isLoading) return <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!spare) return <div className="p-6 text-sm text-muted-foreground">Spare not found.</div>;

  const totalOnHand = spare.stock_total ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/spares" })}><ArrowLeft className="mr-1 h-4 w-4" />Back</Button>
        <div className="flex gap-2">
          {canTransfer && (
            <SpareTransferDialog
              spareId={spare.id}
              trigger={<Button size="sm" variant="outline"><ArrowRightLeft className="mr-1 h-4 w-4" />Transfer</Button>}
            />
          )}
          {canWrite && <ReceiptDialog spareId={spare.id} trigger={<Button size="sm">Record Receipt</Button>} />}
          {canWrite && canAdjust && <AdjustStockDialog spareId={spare.id} trigger={<Button size="sm" variant="outline">Adjust Stock</Button>} />}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span><span className="font-mono text-base mr-2">{spare.code}</span>{spare.name}</span>
            <StockBadge qty={totalOnHand} min={spare.min_qty} />
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm md:grid-cols-4">
          <div><div className="text-xs text-muted-foreground">Unit</div><div>{spare.unit}</div></div>
          <div><div className="text-xs text-muted-foreground">Rate</div><div>₹ {Number(spare.rate).toFixed(2)}</div></div>
          <div><div className="text-xs text-muted-foreground">GST</div><div>{Number(spare.gst_rate).toFixed(2)}%</div></div>
          <div><div className="text-xs text-muted-foreground">Min qty</div><div>{spare.min_qty}</div></div>
          <div><div className="text-xs text-muted-foreground">Vendor</div><div>{spare.vendor?.name ?? "—"}</div></div>
          <div><div className="text-xs text-muted-foreground">On hand</div><div>{totalOnHand}</div></div>
          <div className="md:col-span-2"><div className="text-xs text-muted-foreground">Description</div><div>{spare.description || "—"}</div></div>
        </CardContent>
      </Card>

      <Tabs defaultValue="stock">
        <TabsList>
          <TabsTrigger value="stock">Stock by Store</TabsTrigger>
          <TabsTrigger value="txns">Transactions</TabsTrigger>
          <TabsTrigger value="allocations">Allocation History</TabsTrigger>
          {canWrite && <TabsTrigger value="edit"><Pencil className="mr-1 h-3 w-3" />Edit</TabsTrigger>}
        </TabsList>
        <TabsContent value="stock">
          <Card><CardContent className="p-0">
            {stock.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No stock yet.</div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Store</TableHead><TableHead>Rack/Bin</TableHead><TableHead>Batch</TableHead>
                  <TableHead>Qty</TableHead><TableHead>Expiry</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {stock.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.store_location}</TableCell>
                      <TableCell className="text-xs">{s.rack_location ?? "—"}</TableCell>
                      <TableCell className="text-xs">{s.batch_no ?? "—"}</TableCell>
                      <TableCell>{s.qty_on_hand}</TableCell>
                      <TableCell className="text-xs">{s.expiry_date ? <ExpiryBadge date={s.expiry_date} /> : "—"}</TableCell>
                      <TableCell><StockBadge qty={Number(s.qty_on_hand ?? 0)} min={spare.min_qty} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="txns">
          <Card><CardContent className="p-0">
            {txns.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No transactions.</div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>When</TableHead><TableHead>Type</TableHead><TableHead>Store</TableHead>
                  <TableHead>Qty</TableHead><TableHead>Unit cost</TableHead><TableHead>WO / Ref</TableHead><TableHead>By</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {txns.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="text-xs">{fmtDateTime(t.created_at)}</TableCell>
                      <TableCell><TxnTypeBadge type={t.transaction_type as any} /></TableCell>
                      <TableCell className="text-xs">—</TableCell>
                      <TableCell>{t.quantity}</TableCell>
                      <TableCell>₹ {Number(t.unit_cost).toFixed(2)}</TableCell>
                      <TableCell className="font-mono text-xs">{t.wo_number ?? t.reference ?? "—"}</TableCell>
                      <TableCell className="text-xs">{t.actor_name ?? t.actor_email ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="allocations">
          <Card><CardContent className="p-0">
            {allocations.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">No allocations yet.</div>
            ) : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Indent</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Allocated</TableHead>
                  <TableHead className="text-right">Consumed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Allocated At</TableHead>
                  <TableHead>Fully Consumed</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {allocations.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">
                        {a.indent?.store_indent_no ? (
                          <Link to="/store-indents/$id" params={{ id: a.store_indent_id }} className="text-primary hover:underline">
                            {a.indent.store_indent_no}
                          </Link>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-xs">{a.department?.name ?? "—"}</TableCell>
                      <TableCell className="text-xs">{resolveLocationPath(locationNodes, a.location_id) || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{Number(a.qty_allocated)}</TableCell>
                      <TableCell className="text-right tabular-nums">{Number(a.qty_consumed)}</TableCell>
                      <TableCell><Badge variant="outline">{a.status}</Badge></TableCell>
                      <TableCell className="text-xs">{fmtDateTime(a.allocated_at)}</TableCell>
                      <TableCell className="text-xs">{a.fully_consumed_at ? fmtDateTime(a.fully_consumed_at) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent></Card>
        </TabsContent>
        {canWrite && (
          <TabsContent value="edit">
            {spare.item_type === "consumable" ? (
              <ConsumableForm initial={spare} />
            ) : (
              <SpareForm initial={spare} />
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
